import { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { AtsCandidatePageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { UploadCvsModal } from 'modules/ats/components/UploadCvsModal'
import { UploadCvsWidget, useCvBulkUpload } from 'modules/ats/components/UploadCvsWidget'
import { CandidatesDataTable } from 'modules/ats/components/candidates/CandidatesDataTable'
import { isCvBulkUploadCompleted } from 'modules/ats/lib/utils/cvBulkUpload'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import type { ResponsivePagePrimaryAction } from 'modules/layout/components/ResponsivePage/_PageHeader/types'

const CandidatesPage = () => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [showUploadCvsModal, setShowUploadCvsModal] = useState(false)
  const { data, loading: loadingPageData } = useQuery(AtsCandidatePageDocument, {
    variables: { companyId: company.id },
  })

  useEffect(() => {
    track(trackingEvents.ats.newCandidatesPageView)
  }, [])

  const canAddCandidate = data?.ats._policies.manageJobPostings.can
  const canImportResources = data?.universalImporter._policies.importResources.can

  const atsBulkCandidatesImportFeatureFlag = data?.features.featuresConnection.nodes.find(
    (featureFlag) => featureFlag.name === 'ats_bulk_import'
  )
  const atsImportersFeatureFlag = data?.features.featuresConnection.nodes.find(
    (featureFlag) => featureFlag.name === 'ats_importers'
  )
  const atsBulkActionsFeatureFlag =
    data?.features.featuresConnection.nodes.find(
      (featureFlag) => featureFlag.name === 'ats_bulk_actions'
    )?.enabled || false
  const showImportResources = !!(canImportResources && atsImportersFeatureFlag?.enabled)

  const primaryActions: ResponsivePagePrimaryAction[] = [
    ...(canAddCandidate
      ? [
          {
            to: navigation.ats.candidates.new.fullPath(),
            icon: 'add',
            label: i18n.t('ats.candidates.new_candidate'),
          } as const,
        ]
      : []),
  ]

  const { cvBulkUpload, loading: loadingCvBulkUpload } = useCvBulkUpload()

  const showUploadCvsButton =
    canAddCandidate && (!cvBulkUpload || isCvBulkUploadCompleted(cvBulkUpload?.status))

  if (showUploadCvsButton) {
    primaryActions.push({
      label: i18n.t('ats.candidates.upload_cvs'),
      onClick: () => setShowUploadCvsModal(true),
      variant: 'secondary',
    } as const)
  }

  const candidateFilterOptions = data
    ? {
        candidateSources: data.ats.candidateSourcesConnection.nodes,
        jobPostings: data.ats.jobPostingsConnection.nodes,
        legalEntities: data.companies.legalEntitiesConnection.nodes,
        locationEntities: data.locations.locationsConnection.nodes,
        rejectionReasons: data.ats.rejectionReasonsConnection.nodes,
        tags: data.ats.tags,
        teams: data.teams.teamsConnection.nodes,
      }
    : null

  const isConsentLimitEnabled =
    data?.ats.dataRetentionConfigurationsConnection.nodes[0]?.consentLimitEnabled || false

  return (
    <ResponsivePage.Body
      error={null}
      loading={loadingPageData || loadingCvBulkUpload}
      variant="full"
      actions={{
        primary: [
          {
            to: navigation.settings.ats.fullPath(),
            icon: 'settings',
            variant: 'secondary',
            label: i18n.t('common.settings'),
          } as const,
        ],
      }}
      showHeader
      header={{
        title: i18n.t('ats.candidates.title'),
        illustration: 'user-search',
      }}
      widget={
        (showUploadCvsButton || canAddCandidate) && (
          <Box flexDirection="row" justifyContent="flexEnd" gap="s8">
            {canAddCandidate && (
              <Button.Split
                iconName="add"
                label={i18n.t('ats.candidates.new_candidate')}
                type="primary"
                aria-label={i18n.t('ats.candidates.new_candidate')}
                dropdownAriaLabel={i18n.t('common.more_options')}
                onClick={() => navigate(navigation.ats.candidates.new.fullPath())}
              >
                <Button.Split.Option
                  onClick={() =>
                    navigate(navigation.ats.candidates.newEmployeeCandidate.fullPath())
                  }
                  aria-label={i18n.t('ats.candidates.add_employee')}
                >
                  {i18n.t('ats.candidates.add_employee')}
                </Button.Split.Option>

                {atsBulkCandidatesImportFeatureFlag?.enabled && (
                  <Button.Split.Option
                    onClick={() => navigate(navigation.ats.candidates.import.fullPath())}
                    aria-label={i18n.t('ats.candidates.popup_options.import_title')}
                  >
                    {i18n.t('ats.candidates.popup_options.import_title')}
                  </Button.Split.Option>
                )}
                {showImportResources && (
                  <Button.Split.Option
                    onClick={() =>
                      navigate(
                        navigation.ats.candidates.importCandidates.fullPath().replace(/\*+$/, '')
                      )
                    }
                    aria-label={i18n.t('ats.candidates.candidates_importer')}
                    iconName="upload"
                  >
                    {i18n.t('ats.candidates.candidates_importer')}
                  </Button.Split.Option>
                )}
              </Button.Split>
            )}

            {showUploadCvsButton && (
              <Button onClick={() => setShowUploadCvsModal(true)} type="secondary">
                {i18n.t('ats.candidates.upload_cvs')}
              </Button>
            )}
          </Box>
        )
      }
    >
      {!!cvBulkUpload && (
        <Box paddingBottom="s24">
          <UploadCvsWidget cvBulkUpload={cvBulkUpload} />
        </Box>
      )}
      {candidateFilterOptions && (
        <CandidatesDataTable
          candidateFilterOptions={candidateFilterOptions}
          isConsentLimitEnabled={isConsentLimitEnabled}
          isBulkActionsEnabled={atsBulkActionsFeatureFlag}
        />
      )}
      <Outlet />
      {showUploadCvsModal && <UploadCvsModal onClose={() => setShowUploadCvsModal(false)} />}
    </ResponsivePage.Body>
  )
}

export default CandidatesPage
